import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@habitech/call-to-action';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import Icon from 'src/components/common/Icon';
import { LoadingOverlay } from 'src/components/common/LoadingOverlay';
import {
    selectPortfolioAssignError,
    selectPortfolioAssigning,
    selectPortfolioFullProperties,
    selectPortfolioFullSlots,
    selectPortfolioFullTotals,
} from 'src/redux/user_portfolio/user_portfolio.selectors';
import { PortfolioSlot } from 'src/redux/user_portfolio/user_portfolio.types';
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from 'src/redux/user_portfolio/user_portfolio.constants';
import { cancelSlot, manageSlotPairs } from 'src/redux/user_portfolio/thunks';
import { useLazyPortfolioFull } from '../hooks/useLazyPortfolioFull';
import PropertiesPanel from './PropertiesPanel';
import SlotCategorySection from './SlotCategorySection';
import LiberateConfirmModal from './LiberateConfirmModal';
import ReplaceConfirmModal from './ReplaceConfirmModal';
import {
    partitionSlots,
    SlotCategory,
    slotCategory,
    splitSlotsByCategory,
} from './assignmentsHelpers';
import {
    AssignmentsRoot,
    AssignCtaButton,
    Board,
    HintBanner,
    InlineSpinner,
    LiberateBarButton,
    SaveError,
    SelectionActions,
    SelectionBar,
    SelectionCount,
    SelectionInfo,
    SelectionTexts,
    SlotsColumn,
    Toast,
    Toolbar,
} from './VisibilityAssignment.styles';

const CATEGORY_LABEL: Record<SlotCategory, string> = {
    prime: 'Prime',
    destacado: 'Destacados',
};

/** Etiqueta singular por categoría (para el toast de liberación). */
const CATEGORY_LABEL_SINGULAR: Record<SlotCategory, string> = {
    prime: 'Prime',
    destacado: 'Destacado',
};

/**
 * Reordena los slots disponibles para que la asignación arranque en `startSlotId`
 * (click/drop sobre un slot puntual), manteniendo el resto en orden circular.
 */
const orderAvailableFromStart = (
    available: PortfolioSlot[],
    startSlotId?: number
): PortfolioSlot[] => {
    if (startSlotId == null) return available;
    const startIdx = available.findIndex((slot) => slot.id === startSlotId);
    if (startIdx <= 0) return available;
    return [...available.slice(startIdx), ...available.slice(0, startIdx)];
};

/** Construye el mensaje del toast tras una asignación exitosa. */
const buildAssignToastMessage = (
    category: SlotCategory,
    assignedCount: number,
    leftCount: number
): string => {
    const label = CATEGORY_LABEL[category];
    const base =
        assignedCount === 1
            ? `1 propiedad asignada a ${label}.`
            : `${assignedCount} propiedades asignadas a ${label}.`;
    return leftCount > 0 ? `${base} ${leftCount} sin asignar por falta de slots.` : base;
};

const EMPTY_COPY = 'Busca un cliente por correo electrónico para ver su portafolio.';

interface LiberateState {
    open: boolean;
    slotIds: number[];
    propertyIds: number[];
    category: SlotCategory | null;
}

const CLOSED_LIBERATE: LiberateState = {
    open: false,
    slotIds: [],
    propertyIds: [],
    category: null,
};

/** Estado del modal de confirmación de reemplazo (arrastrar libre sobre ocupado). */
interface ReplaceState {
    open: boolean;
    slot: PortfolioSlot | null;
    /** Número visible del slot (posición 1-based dentro de los ocupados). */
    slotNumber: number | null;
    /** Id de la propiedad libre que entrará al slot. */
    newPropertyId: number | null;
}

const CLOSED_REPLACE: ReplaceState = {
    open: false,
    slot: null,
    slotNumber: null,
    newPropertyId: null,
};

/** Toast de confirmación (asignar/liberar). */
interface ActionToast {
    message: string;
}

interface AssignCtaProps {
    category: SlotCategory;
    icon: string;
    label: string;
    pending: boolean;
    disabled: boolean;
    onClick: () => void;
}

/** Botón CTA de asignación (Prime/Destacados) con estado de carga. */
const AssignCta = ({ category, icon, label, pending, disabled, onClick }: AssignCtaProps) => (
    <AssignCtaButton
        type="button"
        data-id={`assign-to-${category}`}
        onClick={onClick}
        disabled={disabled}
    >
        {pending ? (
            <InlineSpinner aria-hidden />
        ) : (
            <span className="cta-icon" aria-hidden>
                {icon}
            </span>
        )}
        {pending ? 'Asignando…' : label}
    </AssignCtaButton>
);

interface PropertySelectionBarProps {
    selectedCount: number;
    pendingAction: SlotCategory | null;
    assigning: boolean;
    onCancel: () => void;
    onAssignPrime: () => void;
    onAssignDestacado: () => void;
}

/** Barra flotante con las propiedades libres seleccionadas y sus acciones. */
const PropertySelectionBar = ({
    selectedCount,
    pendingAction,
    assigning,
    onCancel,
    onAssignPrime,
    onAssignDestacado,
}: PropertySelectionBarProps) => (
    <SelectionBar data-id="selection-bar">
        <SelectionInfo>
            <SelectionCount>{selectedCount}</SelectionCount>
            <SelectionTexts>
                <span className="title">
                    {selectedCount === 1
                        ? '1 propiedad seleccionada'
                        : `${selectedCount} propiedades seleccionadas`}
                </span>
                <span className="subtitle">Listas para asignar</span>
            </SelectionTexts>
        </SelectionInfo>
        <SelectionActions>
            <Button
                type="button"
                variant="ghost"
                size="medium"
                originType="pcom"
                dataId="selection-cancel"
                onClick={onCancel}
                disabled={assigning}
            >
                Cancelar
            </Button>
            <AssignCta
                category="prime"
                icon="♔"
                label="Asignar a Prime"
                pending={pendingAction === 'prime'}
                disabled={assigning}
                onClick={onAssignPrime}
            />
            <AssignCta
                category="destacado"
                icon="☆"
                label="Asignar a Destacados"
                pending={pendingAction === 'destacado'}
                disabled={assigning}
                onClick={onAssignDestacado}
            />
        </SelectionActions>
    </SelectionBar>
);

interface OccupiedSelectionBarProps {
    selectedCount: number;
    assigning: boolean;
    onCancel: () => void;
    onLiberate: () => void;
}

/** Barra flotante con los slots ocupados seleccionados para liberar en lote. */
const OccupiedSelectionBar = ({
    selectedCount,
    assigning,
    onCancel,
    onLiberate,
}: OccupiedSelectionBarProps) => (
    <SelectionBar data-id="occupied-selection-bar" $large>
        <SelectionInfo>
            <SelectionCount $tone="dark" $large>
                {selectedCount}
            </SelectionCount>
            <SelectionTexts>
                <span className="title">
                    {selectedCount === 1
                        ? '1 slot seleccionado'
                        : `${selectedCount} slots seleccionados`}
                </span>
                <span className="subtitle">Ocupados</span>
            </SelectionTexts>
        </SelectionInfo>
        <SelectionActions>
            <LiberateBarButton
                type="button"
                data-id="occupied-selection-cancel"
                onClick={onCancel}
                disabled={assigning}
            >
                Cancelar
            </LiberateBarButton>
            <LiberateBarButton
                type="button"
                data-id="occupied-selection-liberate"
                onClick={onLiberate}
                disabled={assigning}
            >
                <Icon icon="pcom-icon-delete" size={16} />
                Liberar slots
            </LiberateBarButton>
        </SelectionActions>
    </SelectionBar>
);

/**
 * Tab "Asignaciones" — Asignación de Visibilidad.
 *
 * Rediseño en dos paneles: a la izquierda el portafolio de propiedades del
 * anunciante y a la derecha los espacios Prime y Destacados (slots) separados en
 * ocupados y disponibles. Los datos provienen de `commercial_panel/portfolio`.
 *
 * - Asignar: seleccionar una propiedad libre y pulsar "Asignar aquí" en un slot
 *   disponible, o arrastrar la propiedad sobre el slot (`commercial_panel/slots/manage`).
 * - Reemplazar: arrastrar una propiedad libre sobre un slot ocupado; la propiedad
 *   previa vuelve a estado libre (escenario SWAP de `commercial_panel/slots/manage`).
 * - Liberar: desde el menú de cada slot ocupado o seleccionando varios con
 *   checkbox y liberándolos en lote (`commercial_panel/cancel`).
 */
const Assignments = () => {
    const dispatch = useAppDispatch();
    const { submittedQuery, loading, error } = useLazyPortfolioFull();
    const slots = useAppSelector(selectPortfolioFullSlots);
    const properties = useAppSelector(selectPortfolioFullProperties);
    const assigning = useAppSelector(selectPortfolioAssigning);
    const assignError = useAppSelector(selectPortfolioAssignError);
    const { totalProperties } = useAppSelector(selectPortfolioFullTotals);

    // Tamaño de página client-side (sobre el conjunto completo ya cargado).
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
    const [selectedOccupiedIds, setSelectedOccupiedIds] = useState<number[]>([]);
    const [, setDraggingPropertyId] = useState<number | null>(null);
    const [actionHint, setActionHint] = useState<string | null>(null);
    const [toast, setToast] = useState<ActionToast | null>(null);
    const [liberate, setLiberate] = useState<LiberateState>(CLOSED_LIBERATE);
    const [replace, setReplace] = useState<ReplaceState>(CLOSED_REPLACE);
    // Categoría que se está asignando, para mostrar el spinner en el CTA pulsado.
    const [pendingAction, setPendingAction] = useState<SlotCategory | null>(null);

    const { prime, destacado } = useMemo(() => splitSlotsByCategory(slots), [slots]);
    const selectedCount = selectedPropertyIds.length;

    // Mapa id_property -> propiedad, para mostrar el nombre en los slots ocupados.
    const propertyById = useMemo(() => {
        const map: Record<number, (typeof properties)[number]> = {};
        properties.forEach((property) => {
            map[property.id_property] = property;
        });
        return map;
    }, [properties]);

    // El toast de confirmación se cierra solo tras unos segundos.
    useEffect(() => {
        if (!toast) return undefined;
        const timer = window.setTimeout(() => setToast(null), 6000);
        return () => window.clearTimeout(timer);
    }, [toast]);

    const resetSelection = () => {
        setSelectedPropertyIds([]);
        setSelectedOccupiedIds([]);
        setActionHint(null);
    };

    const toggleProperty = (id: number) => {
        setActionHint(null);
        setSelectedPropertyIds((prev) =>
            prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
        );
    };

    const selectAllFree = (freeIds: number[], checked: boolean) => {
        setActionHint(null);
        setSelectedPropertyIds((prev) => {
            const withoutFree = prev.filter((id) => !freeIds.includes(id));
            return checked ? [...withoutFree, ...freeIds] : withoutFree;
        });
    };

    /**
     * Asigna las propiedades indicadas a los slots disponibles de una categoría.
     * Si se pasa `startSlotId` (click/drop sobre un slot puntual) la asignación
     * arranca en ese slot. Empareja cada propiedad con un slot disponible; si hay
     * menos slots que propiedades, asigna las que quepan e informa el resto.
     */
    const assignProperties = (
        category: SlotCategory,
        propertyIds: number[],
        startSlotId?: number
    ) => {
        if (assigning || propertyIds.length === 0) return;
        const categorySlots = category === 'prime' ? prime : destacado;
        const available = partitionSlots(categorySlots).available;
        const ordered = orderAvailableFromStart(available, startSlotId);

        const pairs = propertyIds
            .slice(0, ordered.length)
            .map((propertyId, i) => ({ slotId: ordered[i].id, propertyId }));

        if (pairs.length === 0) {
            setActionHint(`No hay slots ${CATEGORY_LABEL[category]} disponibles para asignar.`);
            return;
        }

        const left = propertyIds.length - pairs.length;
        setPendingAction(category);
        dispatch(manageSlotPairs({ pairs, reason: '' }))
            .unwrap()
            .then(() => {
                resetSelection();
                setToast({ message: buildAssignToastMessage(category, pairs.length, left) });
            })
            .catch(() => {
                /* El error se refleja vía assignError. */
            })
            .finally(() => setPendingAction(null));
    };

    const handleAssignClick = (slot: PortfolioSlot) => {
        if (assigning) return;
        if (selectedPropertyIds.length === 0) {
            setToast({ message: 'Selecciona al menos una propiedad libre para asignarla aquí.' });
            return;
        }
        assignProperties(slotCategory(slot), selectedPropertyIds, slot.id);
    };

    const handleDropProperty = (slot: PortfolioSlot, propertyIds: number[]) => {
        if (assigning) return;
        setDraggingPropertyId(null);
        assignProperties(slotCategory(slot), propertyIds, slot.id);
    };

    /**
     * Al arrastrar una propiedad libre sobre un slot ocupado abre el modal de
     * confirmación con la propiedad actual (saliente) y la nueva (entrante). El
     * slot solo admite una propiedad, así que se usa la primera de las arrastradas.
     */
    const handleReplaceProperty = (
        slot: PortfolioSlot,
        propertyIds: number[],
        slotNumber: number
    ) => {
        if (assigning || propertyIds.length === 0) return;
        setDraggingPropertyId(null);
        const [newPropertyId] = propertyIds;
        // Si se suelta la misma propiedad que ya ocupa el slot, no hay nada que hacer.
        if (newPropertyId === slot.property_id) return;
        setReplace({ open: true, slot, slotNumber, newPropertyId });
    };

    /**
     * Ejecuta el reemplazo confirmado (escenario SWAP): la nueva propiedad ocupa el
     * slot y la anterior vuelve a estado libre.
     */
    const confirmReplace = () => {
        const { slot, slotNumber, newPropertyId } = replace;
        if (!slot || newPropertyId == null) return;

        dispatch(
            manageSlotPairs({
                pairs: [
                    { slotId: slot.id, propertyId: newPropertyId, oldPropertyId: slot.property_id },
                ],
                reason: '',
            })
        )
            .unwrap()
            .then(() => {
                setReplace(CLOSED_REPLACE);
                resetSelection();
                const slotText = slotNumber != null ? String(slotNumber).padStart(2, '0') : '';
                setToast({
                    message: slotText ? `Slot ${slotText} reemplazado.` : 'Slot reemplazado.',
                });
            })
            .catch(() => {
                setReplace(CLOSED_REPLACE);
            });
    };

    const toggleOccupied = (slotId: number) => {
        setSelectedOccupiedIds((prev) =>
            prev.includes(slotId) ? prev.filter((id) => id !== slotId) : [...prev, slotId]
        );
    };

    const selectAllOccupied = (occupiedIds: number[], checked: boolean) => {
        setSelectedOccupiedIds((prev) => {
            const withoutCategory = prev.filter((id) => !occupiedIds.includes(id));
            return checked ? [...withoutCategory, ...occupiedIds] : withoutCategory;
        });
    };

    const clearOccupied = () => setSelectedOccupiedIds([]);

    const liberateSingle = (slot: PortfolioSlot) => {
        setLiberate({
            open: true,
            slotIds: [slot.id],
            propertyIds: slot.property_id != null ? [slot.property_id] : [],
            category: slotCategory(slot),
        });
    };

    const liberateBatch = () => {
        const slotIds = selectedOccupiedIds;
        if (slotIds.length === 0) return;
        const selectedSlots = slots.filter((slot) => slotIds.includes(slot.id));
        const propertyIds = Array.from(
            new Set(
                selectedSlots
                    .map((slot) => slot.property_id)
                    .filter((id): id is number => id != null)
            )
        );
        // Si todos los seleccionados son de la misma categoría se etiqueta; si
        // están mezclados (Prime + Destacados) se muestra solo el total.
        const categories = Array.from(new Set(selectedSlots.map((slot) => slotCategory(slot))));
        const category = categories.length === 1 ? categories[0] : null;
        setLiberate({ open: true, slotIds, propertyIds, category });
    };

    const confirmLiberate = (reason: string) => {
        const { slotIds, propertyIds, category } = liberate;
        dispatch(cancelSlot({ slotIds, propertyIds, reason }))
            .unwrap()
            .then(() => {
                setLiberate(CLOSED_LIBERATE);
                resetSelection();
                const count = slotIds.length;
                const liberatedText = count === 1 ? '1 slot liberado' : `${count} slots liberados`;
                const catText = category ? ` ${CATEGORY_LABEL_SINGULAR[category]}` : '';
                setToast({
                    message: `${liberatedText}${catText}. Las propiedades vuelven a estado libre.`,
                });
            })
            .catch(() => {
                setLiberate(CLOSED_LIBERATE);
            });
    };

    if (!submittedQuery) {
        return <AssignmentsRoot>{EMPTY_COPY}</AssignmentsRoot>;
    }

    // Solo se ocupa toda la vista con el error en la carga inicial (sin datos).
    // Un fallo de la recarga en segundo plano (tras asignar/liberar) NO debe
    // borrar el panel si ya hay slots cargados en pantalla.
    if (error && slots.length === 0) {
        return <AssignmentsRoot>{error}</AssignmentsRoot>;
    }

    const canAssignByClick = selectedCount > 0;

    return (
        <AssignmentsRoot>
            <Toolbar>
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                        Asignación de Visibilidad
                    </h2>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b6b6b' }}>
                        Optimiza los espacios Prime y Destacados de tu anunciante
                    </p>
                </div>
            </Toolbar>

            <HintBanner $variant={actionHint ? 'warn' : 'info'}>
                <Icon
                    icon={actionHint ? 'pcom-icon-warning-filled' : 'pcom-icon-information-filled'}
                    size={16}
                />
                {actionHint ||
                    (canAssignByClick
                        ? `${selectedCount} propiedad(es) seleccionada(s): pulsa "Asignar aquí" en un slot disponible, arrástralas, o usa "Asignar a Prime/Destacados".`
                        : 'Selecciona una o varias propiedades libres y pulsa "Asignar aquí", o arrástralas a un slot disponible.')}
            </HintBanner>

            {assignError && <SaveError>{assignError}</SaveError>}

            <Board
                style={{
                    marginTop: 16,
                    opacity: loading ? 0.6 : 1,
                    position: 'relative',
                }}
            >
                {assigning && <LoadingOverlay />}
                <PropertiesPanel
                    properties={properties}
                    slots={slots}
                    total={totalProperties}
                    selectedPropertyIds={selectedPropertyIds}
                    onToggleProperty={toggleProperty}
                    onSelectAllFree={selectAllFree}
                    onClearSelection={resetSelection}
                    onDragStateChange={setDraggingPropertyId}
                />

                <SlotsColumn>
                    <SlotCategorySection
                        category="prime"
                        slots={prime}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPageSizeChange={setPageSize}
                        propertyById={propertyById}
                        selectedOccupiedIds={selectedOccupiedIds}
                        canAssignByClick={canAssignByClick}
                        selectedCount={selectedCount}
                        onToggleOccupied={toggleOccupied}
                        onSelectAllOccupied={selectAllOccupied}
                        onLiberateSingle={liberateSingle}
                        onClearOccupied={clearOccupied}
                        onAssignClick={handleAssignClick}
                        onDropProperty={handleDropProperty}
                        onReplaceProperty={handleReplaceProperty}
                    />
                    <SlotCategorySection
                        category="destacado"
                        slots={destacado}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPageSizeChange={setPageSize}
                        propertyById={propertyById}
                        selectedOccupiedIds={selectedOccupiedIds}
                        canAssignByClick={canAssignByClick}
                        selectedCount={selectedCount}
                        onToggleOccupied={toggleOccupied}
                        onSelectAllOccupied={selectAllOccupied}
                        onLiberateSingle={liberateSingle}
                        onClearOccupied={clearOccupied}
                        onAssignClick={handleAssignClick}
                        onDropProperty={handleDropProperty}
                        onReplaceProperty={handleReplaceProperty}
                    />
                </SlotsColumn>
            </Board>

            {selectedCount > 0 && (
                <PropertySelectionBar
                    selectedCount={selectedCount}
                    pendingAction={pendingAction}
                    assigning={assigning}
                    onCancel={resetSelection}
                    onAssignPrime={() => assignProperties('prime', selectedPropertyIds)}
                    onAssignDestacado={() => assignProperties('destacado', selectedPropertyIds)}
                />
            )}

            {selectedOccupiedIds.length > 0 && (
                <OccupiedSelectionBar
                    selectedCount={selectedOccupiedIds.length}
                    assigning={assigning}
                    onCancel={clearOccupied}
                    onLiberate={liberateBatch}
                />
            )}

            <LiberateConfirmModal
                open={liberate.open}
                slotCount={liberate.slotIds.length}
                category={liberate.category}
                loading={assigning}
                onCancel={() => setLiberate(CLOSED_LIBERATE)}
                onConfirm={confirmLiberate}
            />

            <ReplaceConfirmModal
                open={replace.open}
                category={replace.slot ? slotCategory(replace.slot) : null}
                slotNumber={replace.slotNumber}
                currentProperty={
                    replace.slot?.property_id != null
                        ? propertyById[replace.slot.property_id]
                        : undefined
                }
                currentPropertyId={replace.slot?.property_id ?? null}
                newProperty={
                    replace.newPropertyId != null
                        ? propertyById[replace.newPropertyId]
                        : undefined
                }
                newPropertyId={replace.newPropertyId}
                loading={assigning}
                onCancel={() => setReplace(CLOSED_REPLACE)}
                onConfirm={confirmReplace}
            />

            {toast && (
                <Toast data-id="assign-toast" role="status">
                    <span>{toast.message}</span>
                </Toast>
            )}
        </AssignmentsRoot>
    );
};

export default Assignments;
